/* quick-add.js — drives #quickadd-drawer */

(function () {
  const drawer    = document.getElementById('quickadd-drawer');
  const backdrop  = drawer?.querySelector('.js-qa-close');
  const closeBtn  = drawer?.querySelector('.QuickAddDrawer__Close');
  const form      = drawer?.querySelector('.QuickAddDrawer__Form');
  const imgEl     = drawer?.querySelector('.js-qa-img');
  const titleEl   = drawer?.querySelector('.js-qa-product');
  const priceEl   = drawer?.querySelector('.js-qa-price');
  const optsRoot  = drawer?.querySelector('.js-qa-options');
  const qtyEl     = drawer?.querySelector('#qa-qty');
  const addBtn    = drawer?.querySelector('.QuickAddDrawer__AddBtn');
  const idInput   = drawer?.querySelector('input[name="id"]');
  const errEl     = drawer?.querySelector('.js-qa-error');

  if (!drawer) return;

  let product = null;       // current product JSON (from data-product)
  let selected = [];        // current option values by index (0..2)
  let variantsById = {};    // quick lookup

  // ---------- helpers ----------

  const money = cents =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })
      .format((Number(cents) || 0) / 100);

  function getVariantImageSrc(v) {
    if (!v) return '';
    if (v.featured_image && v.featured_image.src) return v.featured_image.src;
    if (v.image && v.image.src) return v.image.src;
    return '';
  }

  function getFallbackImageSrc(prod) {
    if (prod?.featured_image?.src) return prod.featured_image.src;
    if (Array.isArray(prod?.images) && prod.images.length) {
      return typeof prod.images[0] === 'string'
        ? prod.images[0]
        : (prod.images[0]?.src || '');
    }
    return '';
  }

  function sizeSrc(src, width) {
    if (!src) return '';
    // If already sized like _300x., leave it
    if (/_\d+x\./.test(src)) return src;
    return src.replace(/(\.[a-zA-Z]{3,4})(\?|$)/, `_${width}x$1$2`);
  }

  function setDrawerImage(src) {
    if (!imgEl) return;
    if (!src) { imgEl.removeAttribute('src'); return; }
    imgEl.src = sizeSrc(src, 300);
    imgEl.alt = product?.title || '';
  }

  function open() {
    drawer.setAttribute('aria-hidden', 'false');
    drawer.classList.add('is-open');
    document.documentElement.style.overflow = 'hidden';
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    // Reset basics (don’t wipe options; they’ll be rebuilt next open)
    try { form?.reset(); } catch(e){}
    errEl.hidden = true;
    errEl.textContent = '';
  }

  function buildLookups() {
    variantsById = {};
    (product?.variants || []).forEach(v => { variantsById[v.id] = v; });
  }

  // ----- Availability helpers -----
  function isValueAvailable(prod, idx, value, currentSel) {
    if (!prod || !prod.variants) return false;
    return prod.variants.some(v => {
      if (!v.available) return false;
      const sel0 = currentSel[0], sel1 = currentSel[1], sel2 = currentSel[2];
      const matchesThis =
        (idx === 0 && v.option1 === value) ||
        (idx === 1 && v.option2 === value) ||
        (idx === 2 && v.option3 === value);
      const matchesOther0 = (idx !== 0 ? (sel0 == null || v.option1 === sel0) : true);
      const matchesOther1 = (idx !== 1 ? (sel1 == null || v.option2 === sel1) : true);
      const matchesOther2 = (idx !== 2 ? (sel2 == null || v.option3 === sel2) : true);
      return matchesThis && matchesOther0 && matchesOther1 && matchesOther2;
    });
  }

  function updateAvailabilityUI(prod, rootEl, currentSel) {
    if (!prod || !rootEl) return;
    rootEl.querySelectorAll('.QAField').forEach((field, fi) => {
      const idx = fi;
      field.querySelectorAll('.QAPill__Input').forEach(input => {
        const val = input.value;
        const pill = input.closest('.QAPill');
        const ok = isValueAvailable(prod, idx, val, currentSel);
        const span = pill.querySelector('span');
        if (ok) {
          input.disabled = false;
          pill.classList.remove('is-oos');
          pill.removeAttribute('aria-disabled');
          if (span) span.textContent = val;
        } else {
          input.disabled = true;
          pill.classList.add('is-oos');
          pill.setAttribute('aria-disabled', 'true');
          if (span && !span.textContent.startsWith('Sold out')) {
            span.textContent = 'Sold out – ' + val;
          }
        }
      });
    });
  }

  // ---------- options & variants ----------

  function uniqueValuesForIndex(variants, idx) {
    const key = `option${idx+1}`;
    const out = [];
    const seen = new Set();
    (variants || []).forEach(v => {
      const val = v?.[key];
      if (val && !seen.has(val)) { seen.add(val); out.push(val); }
    });
    return out;
  }

  function buildOptionsUI() {
    optsRoot.innerHTML = '';
    if (!product?.options || !product?.variants) return;

    // preselect first available variant to seed UI
    const firstAvail = product.variants.find(v => v.available) || product.variants[0];
    selected = [
      firstAvail?.option1 || null,
      firstAvail?.option2 || null,
      firstAvail?.option3 || null
    ];

    product.options.forEach((optName, idx) => {
      const values = uniqueValuesForIndex(product.variants, idx);
      if (!values.length) return;

      const field = document.createElement('div');
      field.className = 'QAField';
      field.innerHTML = `
        <label class="QAField__Label">${optName}</label>
        <div class="QAField__Pills" data-opt-index="${idx}"></div>
      `;
      const pills = field.querySelector('.QAField__Pills');

      values.forEach(val => {
        // Choose a representative variant for this pill (prefer one compatible with current selection)
        const rep =
          product.variants.find(v => {
            const okThis = v[`option${idx+1}`] === val;
            const ok0 = idx === 0 || (selected[0] == null || v.option1 === selected[0]);
            const ok1 = idx === 1 || (selected[1] == null || v.option2 === selected[1]);
            const ok2 = idx === 2 || (selected[2] == null || v.option3 === selected[2]);
            return okThis && ok0 && ok1 && ok2;
          }) ||
          product.variants.find(v => v[`option${idx+1}`] === val);

        const pillId = `qa-${product.id}-${idx}-${String(val).toLowerCase().replace(/[^a-z0-9]+/g,'-')}`;
        const imgSrc = getVariantImageSrc(rep) || getFallbackImageSrc(product);

        const pill = document.createElement('label');
        pill.className = 'QAPill';
        pill.innerHTML = `
          <input
            class="QAPill__Input"
            type="radio"
            name="qa-opt-${product.id}-${idx}"
            id="${pillId}"
            value="${val.replace(/"/g, '&quot;')}"
            ${selected[idx] === val ? 'checked' : ''}
            ${imgSrc ? `data-image-url="${imgSrc}"` : ''}>
          <span>${val}</span>
        `;
        pills.appendChild(pill);
      });

      optsRoot.appendChild(field);
    });

    // seed UI state from selected
    const current = findMatchingVariant();
    updateForVariant(current);
    updateAvailabilityUI(product, optsRoot, selected);
  }

  function findMatchingVariant() {
    if (!product?.variants) return null;
    const [s0, s1, s2] = selected;
    return product.variants.find(v =>
      (s0 == null || v.option1 === s0) &&
      (s1 == null || v.option2 === s1) &&
      (s2 == null || v.option3 === s2)
    ) || null;
  }

function updateForVariant(variant) {
  if (!variant) {
    addBtn.disabled = true;
    idInput.value = '';
    return;
  }

  idInput.value = variant.id;
  addBtn.disabled = !variant.available;

  // Title stays the same
  titleEl.textContent = product?.title || '';

  // IMPORTANT: do NOT touch priceEl here.
  // We clone the PLP price markup when opening the drawer.

  // Image
  const src = getVariantImageSrc(variant) || getFallbackImageSrc(product);
  setDrawerImage(src);
}



  // ---------- openers ----------

  // Buttons like: <button class="js-open-quickadd" data-product='{{ product_json }}'>Quick Add</button>
  document.addEventListener('click', e => {
    const btn = e.target.closest('.js-open-quickadd,[data-open-quickadd]');
    if (!btn) return;

    e.preventDefault();

    try {
      const raw = btn.getAttribute('data-product') || btn.dataset.product;
      product = JSON.parse(raw);
    } catch (err) {
      console.error('QuickAdd: invalid product JSON', err);
      return;
    }

    // seed header + image prior to options render
    titleEl.textContent = product.title || '';

    // Try to clone the PLP price markup (includes your sg-pro-pricing logic)
    const card = btn.closest('.ProductItem');
    const cardPrice = card && card.querySelector('.ProductItem__PriceList');

    if (cardPrice) {
      // Copy the exact HTML that’s already showing on the PLP
      priceEl.innerHTML = cardPrice.innerHTML;
    } else {
      // Fallback – simple price if we can’t find the card (should be rare)
      priceEl.textContent =
        product?.price != null ? money(product.price) :
        (product?.variants?.[0]?.price != null ? money(product.variants[0].price) : '');
    }


    const initialVariant = product.variants?.find(v => v.available) || product.variants?.[0] || null;
    setDrawerImage(getVariantImageSrc(initialVariant) || getFallbackImageSrc(product));

    buildLookups();
    buildOptionsUI();
    open();
  });

  // ---------- close handlers ----------
  [backdrop, closeBtn].forEach(el => el && el.addEventListener('click', close));
  document.addEventListener('keydown', e => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  // ---------- option changes (delegated) ----------
  optsRoot.addEventListener('change', e => {
    const input = e.target.closest('.QAPill__Input');
    if (!input) return;

    const group = input.closest('.QAField__Pills');
    const idx = parseInt(group.getAttribute('data-opt-index'), 10);
    selected[idx] = input.value;

    // swap image immediately if available for this value
    const url = input.getAttribute('data-image-url');
    if (url) setDrawerImage(url);

    const v = findMatchingVariant();
    updateForVariant(v);
    updateAvailabilityUI(product, optsRoot, selected);
  });

  // ---------- submit -> AJAX add ----------
  form.addEventListener('submit', async e => {
    e.preventDefault();
    errEl.hidden = true;

    const id = Number(idInput.value || 0);
    const qty = Math.max(1, parseInt(qtyEl.value || '1', 10));
    if (!id) return;

    addBtn.disabled = true;

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity: qty })
      });
      if (!res.ok) throw new Error('Add to cart failed');

      // Let your secondary drawer (or other cart UI) react
      document.dispatchEvent(new CustomEvent('product:added'));
      close();
    } catch (err) {
      console.error(err);
      addBtn.disabled = false;
      errEl.textContent = 'Sorry, we could not add that to the cart.';
      errEl.hidden = false;
    }
  });
})();
